/* eslint-disable max-len */
import cond from 'lodash/cond';
import { createSelector } from 'reselect';
import { getIsMultisessionEvent, getIsSessionLegacyEvent, getIsTicketedEvent } from 'common/entities/event/selectors';
import { getInstance as getBootstrap } from 'utils/bootstrap';
import { getEventId } from 'common/store/events/selectors';
import { getIsUserAdmin, getIsUserFreemium, getIsUserInDesignerPro } from './user/selectors';

import {
    canAccessEventApprovalFlow as getCanUpdateWorkflowStatusUserProperties,
    canUserAccessAnalytics as getCanUserAccessAnalyticsUserProperties,
    canUserAccessConfirmations,
    canUserAccessContacts,
    canUserAccessEventOverview,
    canUserAccessEventSettings,
    canUserAccessGroupSettings as getCanUserAccessGroupSettings,
    canUserAccessGuestList,
    canUserAccessIntegrations,
    canUserAccessReporting,
    canUserAddToHub as getCanUserAddToHubViaUserProperties,
    canUserCreateEventLevelIntegration,
    canUserCreateTheme as getCanUserCreateTheme,
    canUserDeleteEvents as getCanUserDeleteEventsViaUserProperties,
    canUserDuplicateEvents as getCanUserDuplicateEventsViaUserProperties,
    canUserEditEventLevelIntegrations,
    canUserModifyPublished,
    getCanUserSetVanityUrl,
    getCanUserAccessBlockLibrary,
    getCanUserAccessDynamicLocalizedTagsManager,
    getCanUserAccessEmailSender,
    getCanUserAccessScheduledEmails,
    getCanUserAccessGlobalBrandManager,
    getCanUserAccessTeam,
    getCanUserAccessThemesDashboard,
    getCanUserAccessVirtualEventTouchpoint as getCanUserAccessVirtualEventTouchpointUserProperty,
    getCanUserEditEventPage,
    getCanUserGoToEvent,
    getIsEventMultipleRegistrationFormsEnabled,
    getIsUserUsingRoleBasedPermissions,
    getIsUserVirtualViewOnly,
    getHasOrgDisabledArtificialIntelligenceFeatures,
    getIsCMSCodeEditorEnabled,
    getIsPrivacyManagementEnabled,
} from './userProperties/selectors';

import {
    getCanUpdateWorkflowStatusUserPermission,
    getCanUserAccessAnalyticsUserPermission,
    getCanUserAddTeamMemberUserDashboardUserPermission,
    getCanUserAddToHubUserPermission,
    getCanUserCreateEmailsUserPermission,
    getCanUserScheduleEmailsUserPermission,
    getCanUserCreateEmailTemplatesUserPermission,
    getCanUserCreateEventLevelIntegrationDashboardUserPermission,
    getCanUserCreateEventsUserPermission,
    getCanUserExportEventsUserPermission,
    getCanUserCreateGlobalBrandManagerUserPermission,
    getCanUserCreateGuestUserPermission,
    getCanUserCreateMultipleRegistrationFormsUserPermission,
    getCanUserCreateRegistrationFormUserPermission,
    getCanUserCreateSessionsTabUserPermission,
    getCanUserCreateThemesUserPermission,
    getCanUserCreateTicketsTabUserPermission,
    getCanUserCreateTopLevelIntegrationDashboardUserPermission,
    getCanUserCreateVirtualEventTouchpointUserPermission,
    getCanUserDeleteEmailsUserPermission,
    getCanUserDeleteEmailTemplatesUserPermission,
    getCanUserSendEmailUserPermission,
    getCanUserDeleteEventsUserPermission,
    getCanUserDeleteMultipleRegistrationFormsUserPermission,
    getCanUserDeleteRegistrationFormUserPermission,
    getCanUserDeleteSessionsTabUserPermission,
    getCanUserDeleteTeamMemberUserDashboardUserPermission,
    getCanUserDeleteThemesUserPermission,
    getCanUserDeleteTicketsTabUserPermission,
    getCanUserDeleteTopLevelIntegrationDashboardUserPermission,
    getCanUserDeleteVirtualEventTouchpointUserPermission,
    getCanUserDuplicateEventsUserPermission,
    getCanUserEditConfirmationUserPermission,
    getCanUserEditEmailsUserPermission,
    getCanUserEditEmailTemplatesUserPermission,
    getCanUserEditEventLevelIntegrationDashboardUserPermission,
    getCanUserManageZoomPermission,
    getCanUserDeleteEventLevelIntegrationDashboardUserPermission,
    getCanUserViewEventOverviewUserPermission,
    getCanUserEditEventOverviewUserPermission,
    getCanUserEditEventPageTouchpointUserPermission,
    getCanUserEditEventSettingsUserPermission,
    getCanUserEditGlobalBrandManagerUserPermission,
    getCanUserEditGuestListUserPermission,
    getCanUserEditMultipleRegistrationFormsUserPermission,
    getCanUserEditRegistrationFormUserPermission,
    getCanUserEditSessionsTabUserPermission,
    getCanUserEditTeamMemberUserDashboardUserPermission,
    getCanUserEditThemeUserPermission,
    getCanUserEditTicketsTabUserPermission,
    getCanUserEditTopLevelIntegrationDashboardUserPermission,
    getCanUserEditVanityUrlUserPermission,
    getCanUserEditEventUrlUserPermission,
    getCanUserEditVirtualEventTouchpointUserPermission,
    getCanUserExportGuestUserPermission,
    getCanUserImportGuestsViaCsvPermission,
    getCanUserImportTeamMembersUserDashboardUserPermission,
    getCanUserPublishEventsUserPermission,
    getCanUserPublishGlobalBrandManagerUserPermission,
    getCanUserViewAllTouchpoints,
    getCanUserViewConfirmationUserPermission,
    getCanUserViewContactsDashboardUserPermission,
    getCanUserViewEmailsUserPermission,
    getCanUserViewEmailTemplatesUserPermission,
    getCanUserViewEventLevelIntegrationDashboardUserPermission,
    getCanUserViewEventPageTouchpointUserPermission,
    getCanUserViewEventSettingsUserPermission,
    getCanUserViewGlobalBrandManagerUserPermission,
    getCanUserViewGroupsTabUserPermission,
    getCanUserViewGuestListUserPermission,
    getCanUserViewIntegrationDashboardUserPermission,
    getCanUserViewLibraryFormsUserPermission,
    getCanUserViewMultipleRegistrationFormsUserPermission,
    getCanUserViewRegistrationFormUserPermission,
    getCanUserViewReportingDashboardUserPermission,
    getCanUserViewSessionsTabUserPermission,
    getCanUserViewTeamDashboardUserPermission,
    getCanUserViewThemesDashboardUserPermission,
    getCanUserViewTicketsTabUserPermission,
    getCanUserViewUserDashboardUserPermission,
    getCanUserViewVirtualEventTouchpointUserPermission,
    getIsFetchingCanUserEditTicketsTabUserPermission,
    getCanUserAccessBlockLibraryUserPermission,
    getCanUserAccessTagLibraryUserPermission,
    getCanUserDeleteGuestsUserPermission,
    getCanUserAccessDataAnonymizationUserPermission,
    getCanUserAccessCMSCodeEditor as getCanUserAccessCMSCodeEditorUserPermission,
    getCanUserAccessPrivacyManagementOrgSettingsPermission,
    getCanUserAccessPrivacyManagementEventSettingsPermission,
    getCanUserShareGuestListUserPermission,
} from './userPermissions/selectors';

const getCanUserAccessFormsAndTickets = () => {
    const bootstrap = getBootstrap();
    const { flags } = bootstrap.data;
    return flags.hasFormsAndTicketsAccessWithoutRbac;
};

const getIsFreemiumFeatureRestrictionEnabled = () => {
    const bootstrap = getBootstrap();
    const { flags } = bootstrap.data;
    return flags.enableFreemiumFeatureRestrictionRollout;
};

const getIsAttendancePredictorEnabled = () => {
    const bootstrap = getBootstrap();
    const { isAttendancePredictorEnabled } = bootstrap.data.flags;
    return isAttendancePredictorEnabled;
};

const getIsCurrentEventTicketed = (state) => {
    const eventId = getEventId(state);
    const isCurrentEventTicketedEvent = getIsTicketedEvent(state, eventId);

    return isCurrentEventTicketedEvent;
};

const getIsDataAnonymizationEnabled = () => {
    const bootstrap = getBootstrap();
    const { flags } = bootstrap.data;
    return flags.isDataAnonymizationEnabled;
};

const getIsDoubleOptInEnabled = () => {
    const bootstrap = getBootstrap();
    return bootstrap.get('flags.enabledDoubleOptIn', false);
};

/*
 * Access to the Themes Dashboard
 */
export const getCanUserViewThemesDashboard = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessThemesDashboard,
    getCanUserViewThemesDashboardUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserAccessThemesDashboard,
        canUserViewThemesDashboardUserPermission,
    ) =>
        isUserFreemium ||
        (isUserUsingRoleBasedPermissions ? canUserViewThemesDashboardUserPermission : canUserAccessThemesDashboard),
);

/**
 * Access to the Team Management Top Level Dashboard
 */
export const getCanUserViewTeamDashboard = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessTeam,
    getCanUserViewTeamDashboardUserPermission,
    (isUserFreemium, isUserUsingRoleBasedPermissions, canUserAccessTeam, canUserViewTeamDashboardUserPermission) =>
        cond([
            [() => isUserFreemium, () => false],
            [() => isUserUsingRoleBasedPermissions, () => canUserViewTeamDashboardUserPermission],
            [() => true, () => canUserAccessTeam],
        ])(),
);

/**
 * Access to the Groups tab in Team Dashboard
 */
export const getCanUserViewGroupsTab = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessGroupSettings,
    getCanUserViewGroupsTabUserPermission,
    (isUserFreemium, isUserUsingRoleBasedPermissions, canUserAccessGroupSettings, canUserViewGroupsTabUserPermission) =>
        cond([
            [() => isUserFreemium, () => false],
            [() => isUserUsingRoleBasedPermissions, () => canUserViewGroupsTabUserPermission],
            [() => true, () => canUserAccessGroupSettings],
        ])(),
);

/**
 * Access to viewing the Team Dashboard tab in the menu
 */
export const getCanUserViewTeamDashboardMenuItem = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessTeam,
    (isUserFreemium, isUserUsingRoleBasedPermissions, canUserAccessTeam) => {
        if (!isUserFreemium && isUserUsingRoleBasedPermissions) {
            return true;
        }
        if (!isUserUsingRoleBasedPermissions) {
            return canUserAccessTeam;
        }
        return false;
    },
);

/**
 * Access to the Events tab in the top-level menu
 */
export const getCanUserAccessEventMenuItem = createSelector(
    getIsUserUsingRoleBasedPermissions,
    getCanUserViewEventOverviewUserPermission,
    (isUserUsingRoleBasedPermissions, canViewEvents) => (isUserUsingRoleBasedPermissions ? canViewEvents : true),
);

/**
 * Access to the Block Library tab
 */
export const getCanUserAccessBlockLibraryPermission = createSelector(
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessBlockLibrary,
    getCanUserAccessBlockLibraryUserPermission,
    (isUserUsingRoleBasedPermissions, canUseBlockLibrary, canUserAccessBlockLibrary) => {
        if (!isUserUsingRoleBasedPermissions) return canUseBlockLibrary;
        return canUseBlockLibrary && canUserAccessBlockLibrary;
    },
);

/**
 * Access to the Tag Library tab
 */
export const getCanUserAccessTagLibraryPermission = createSelector(
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessDynamicLocalizedTagsManager,
    getCanUserAccessTagLibraryUserPermission,
    (isUserUsingRoleBasedPermissions, canUseDynamicTags, canUserAccessDynamicTags) => {
        if (!isUserUsingRoleBasedPermissions) return canUseDynamicTags;
        return canUseDynamicTags && canUserAccessDynamicTags;
    },
);

/**
 * Access to the Library tab in the top-level menu
 */
export const getCanUserAccessLibraryMenuItem = createSelector(
    getIsUserUsingRoleBasedPermissions,
    getCanUserViewLibraryFormsUserPermission,
    getCanUserViewThemesDashboardUserPermission,
    getCanUserViewGlobalBrandManagerUserPermission,
    getCanUserAccessTagLibraryPermission,
    getCanUserAccessBlockLibraryPermission,
    (
        isUserUsingRoleBasedPermissions,
        canUseAccessForms,
        canUseAccessThemes,
        canUseAccessBrands,
        canUseDynamicTags,
        canUseBlocks,
    ) => {
        if (!isUserUsingRoleBasedPermissions) return true;
        return canUseAccessForms || canUseAccessThemes || canUseAccessBrands || canUseDynamicTags || canUseBlocks;
    },
);

/*
 * Access to view an event touchpoint
 */
export const getCanUserViewEventPageTouchpoint = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserViewEventPageTouchpointUserPermission,
    (isUserFreemium, isUserUsingRoleBasedPermissions, canUserViewEventPageUserPermission) =>
        isUserFreemium || (isUserUsingRoleBasedPermissions ? canUserViewEventPageUserPermission : true),
);

/**
 * Access to the touchpoint for virtual event building
 */
export const getCanUserAccessVirtualEventTouchpoint = createSelector(
    getIsUserFreemium,
    getCanUserAccessVirtualEventTouchpointUserProperty,
    (isUserFreemium, canUserAccessVirtualEventTouchpoint) => isUserFreemium || canUserAccessVirtualEventTouchpoint,
);

/**
 * Access to the Reporting Top Level Dashboard
 */
export const getCanUserViewReportingDashboard = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    canUserAccessReporting,
    getCanUserViewReportingDashboardUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserAccessReportingUserProperties,
        canUserViewReportingDashboardUserPermission,
    ) =>
        isUserFreemium ||
        (isUserUsingRoleBasedPermissions
            ? canUserViewReportingDashboardUserPermission
            : canUserAccessReportingUserProperties),
);

/**
 * Access to view event overview page
 * /events/event-name/overview
 * Can click into an event, see event overview page but can't click into disabled touchpoints
 */
export const getCanUserViewEventOverviewPage = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserGoToEvent,
    getCanUserViewEventOverviewUserPermission,
    (isUserFreemium, isUserUsingRoleBasedPermissions, canUserGoToEvent, canUserViewEventOverviewUserPermission) =>
        isUserFreemium || (isUserUsingRoleBasedPermissions ? canUserViewEventOverviewUserPermission : canUserGoToEvent),
);

/*
 * Access to edit event overview page
 * /events/event-name/overview
 * Can click into an event, and can click into touchpoints
 */
export const getCanUserEditEventOverviewPage = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    canUserAccessEventOverview,
    getCanUserEditEventOverviewUserPermission,
    (isUserFreemium, isUserUsingRoleBasedPermissions, canAccessEventOverview, canUserEditEventOverviewUserPermission) =>
        isUserFreemium ||
        (isUserUsingRoleBasedPermissions ? canUserEditEventOverviewUserPermission : canAccessEventOverview),
);

/**
 * Can click on event but has no view permission to event overview page
 * Directed to the next tab (/guests)
 */
export const getCanUserAccessEventOverviewPage = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    canUserAccessEventOverview,
    getCanUserViewAllTouchpoints,
    getCanUserViewEventPageTouchpoint,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canAccessEventOverview,
        canUserViewAllTouchpoints,
        canUserViewEventPageTouchpoint,
    ) =>
        isUserFreemium ||
        (isUserUsingRoleBasedPermissions
            ? canUserViewAllTouchpoints || canUserViewEventPageTouchpoint
            : canAccessEventOverview),
);

/**
 * Controls if user can see the event tab on event level navigation
 * RBAC with no permission --> sees event tab but we disabled it/
 * with getCanUserAccessEventOverviewPage permission
 *
 * RBAC with permission --> sees event tab
 * Freemium --> sees event tab
 * Enterprise, non RBAC, no permission --> hidden event tab
 * Enterprise, non RBAC, permission --> sees event tab
 */
export const getCanUserViewEventTab = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    canUserAccessEventOverview,
    (isUserFreemium, isUserUsingRoleBasedPermissions, canAccessEventOverview) =>
        isUserFreemium || (isUserUsingRoleBasedPermissions ? true : canAccessEventOverview),
);

/**
 * Access to the view Email Sender
 */
export function getCanUserViewEmails(state) {
    const isUserFreemium = getIsUserFreemium(state);
    // User Properties
    const isUserUsingRoleBasedPermissions = getIsUserUsingRoleBasedPermissions(state);
    const canUserAccessEmailSender = getCanUserAccessEmailSender(state);
    // User Permissions
    const canUserViewEmailsUserPermission = getCanUserViewEmailsUserPermission(state);

    return (
        isUserFreemium || (isUserUsingRoleBasedPermissions ? canUserViewEmailsUserPermission : canUserAccessEmailSender)
    );
}

/**
 * Access to the edit emails
 */
export function getCanUserEditEmails(state) {
    const isUserFreemium = getIsUserFreemium(state);
    // User Properties
    const isUserUsingRoleBasedPermissions = getIsUserUsingRoleBasedPermissions(state);
    const canUserAccessEmailSender = getCanUserAccessEmailSender(state);
    // User Permissions
    const canUserEditEmailsUserPermission = getCanUserEditEmailsUserPermission(state);

    return (
        isUserFreemium || (isUserUsingRoleBasedPermissions ? canUserEditEmailsUserPermission : canUserAccessEmailSender)
    );
}

/**
 * Access to the create emails
 */
export const getCanUserCreateEmails = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessEmailSender,
    getCanUserCreateEmailsUserPermission,
    (isUserFreemium, isUserUsingRoleBasedPermissions, canUserAccessEmailSender, canUserCreateEmailsUserPermission) =>
        isUserFreemium ||
        (isUserUsingRoleBasedPermissions ? canUserCreateEmailsUserPermission : canUserAccessEmailSender),
);

/**
 * Access to schedule emails
 */
export const getCanUserScheduleEmails = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessScheduledEmails,
    getCanUserScheduleEmailsUserPermission,
    (isUserFreemium, isUserUsingRoleBasedPermissions, canUserScheduleEmails, canUserScheduleEmailsUserPermission) =>
        isUserFreemium ||
        (isUserUsingRoleBasedPermissions ? canUserScheduleEmailsUserPermission : canUserScheduleEmails),
);

/**
 * Access to the delete emails
 */
export const getCanUserDeleteEmails = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessEmailSender,
    getCanUserDeleteEmailsUserPermission,
    (isUserFreemium, isUserUsingRoleBasedPermissions, canUserAccessEmailSender, canUserDeleteEmailsUserPermission) =>
        isUserFreemium ||
        (isUserUsingRoleBasedPermissions ? canUserDeleteEmailsUserPermission : canUserAccessEmailSender),
);

/**
 * Access to the view Email Sender
 */
export const getCanUserViewEmailTemplates = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessEmailSender,
    getCanUserViewEmailTemplatesUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserAccessEmailSender,
        canUserViewEmailTemplatesUserPermission,
    ) =>
        isUserFreemium ||
        (isUserUsingRoleBasedPermissions ? canUserViewEmailTemplatesUserPermission : canUserAccessEmailSender),
);

/**
 * Access to delete email templates
 */
export const getCanUserDeleteEmailTemplates = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessEmailSender,
    getCanUserDeleteEmailTemplatesUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserAccessEmailSender,
        canUserDeleteEmailTemplatesUserPermission,
    ) =>
        isUserFreemium ||
        (isUserUsingRoleBasedPermissions ? canUserDeleteEmailTemplatesUserPermission : canUserAccessEmailSender),
);

/**
 * Ability to send email
 */
export const getCanUserSendEmail = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessEmailSender,
    getCanUserSendEmailUserPermission,
    (isUserFreemium, isUserUsingRoleBasedPermissions, canUserAccessEmailSender, canUserSendEmailUserPermission) =>
        isUserFreemium || (isUserUsingRoleBasedPermissions ? canUserSendEmailUserPermission : canUserAccessEmailSender),
);

/**
 * Access to edit email templates
 */
export const getCanUserEditEmailTemplates = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessEmailSender,
    getCanUserEditEmailTemplatesUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserAccessEmailSender,
        canUserEditEmailTemplatesUserPermission,
    ) =>
        !isUserFreemium &&
        (isUserUsingRoleBasedPermissions ? canUserEditEmailTemplatesUserPermission : canUserAccessEmailSender),
);

/**
 * Access to create email templates
 */
export const getCanUserCreateEmailTemplates = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessEmailSender,
    getCanUserCreateEmailTemplatesUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserAccessEmailSender,
        canUserCreateEmailTemplatesUserPermission,
    ) =>
        !isUserFreemium &&
        (isUserUsingRoleBasedPermissions ? canUserCreateEmailTemplatesUserPermission : canUserAccessEmailSender),
);

/**
 * Access to viewing the User Dashboard tab in Team Management
 */
export const getCanUserViewUsersTab = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessTeam,
    getCanUserViewUserDashboardUserPermission,
    (isUserFreemium, isUserUsingRoleBasedPermissions, canUserAccessTeam, canUserViewUserDashboardUserPermission) =>
        !isUserFreemium &&
        (isUserUsingRoleBasedPermissions ? canUserViewUserDashboardUserPermission : canUserAccessTeam),
);

/**
 * Access to add team users in the User Dashboard tab
 */
export const getCanUserAddTeamUser = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessTeam,
    getCanUserAddTeamMemberUserDashboardUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserAccessTeam,
        canUserAddTeamMemberUserDashboardUserPermission,
    ) =>
        cond([
            [() => isUserFreemium, () => false],
            [() => isUserUsingRoleBasedPermissions, () => canUserAddTeamMemberUserDashboardUserPermission],
            [() => true, () => canUserAccessTeam],
        ])(),
);

/**
 * Access to import team users in the User Dashboard tab
 */
export const getCanUserImportTeamUsers = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessTeam,
    getCanUserImportTeamMembersUserDashboardUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserAccessTeam,
        canUserImportTeamMembersUserDashboardUserPermission,
    ) =>
        !isUserFreemium &&
        (isUserUsingRoleBasedPermissions ? canUserImportTeamMembersUserDashboardUserPermission : canUserAccessTeam),
);

/**
 * Access to edit team users in the User Dashboard tab
 */
export const getCanUserEditTeamUser = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessTeam,
    getCanUserEditTeamMemberUserDashboardUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserAccessTeam,
        canUserEditTeamMemberUserDashboardUserPermission,
    ) =>
        cond([
            [() => isUserFreemium, () => false],
            [() => isUserUsingRoleBasedPermissions, () => canUserEditTeamMemberUserDashboardUserPermission],
            [() => true, () => canUserAccessTeam],
        ])(),
);

/**
 * Access to delete team users in the User Dashboard tab
 */
export const getCanUserDeleteTeamUser = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessTeam,
    getCanUserDeleteTeamMemberUserDashboardUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserAccessTeam,
        canUserDeleteTeamMemberUserDashboardUserPermission,
    ) =>
        cond([
            [() => isUserFreemium, () => false],
            [() => isUserUsingRoleBasedPermissions, () => canUserDeleteTeamMemberUserDashboardUserPermission],
            [() => true, () => canUserAccessTeam],
        ])(),
);

//= ===================
//   INTEGRATIONS
//= ==================

/**
 * Access to view Integrations Dashboard
 */
export const getCanUserViewIntegrationDashboard = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    canUserAccessIntegrations,
    getCanUserViewIntegrationDashboardUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserAccessIntegration,
        canUserViewIntegrationDashboardUserPermission,
    ) =>
        !isUserFreemium &&
        (isUserUsingRoleBasedPermissions ? canUserViewIntegrationDashboardUserPermission : canUserAccessIntegration),
);

/**
 * Access to Edit ToplevelIntegrations Dashboard
 */
export const getCanUserEditTopLevelIntegrations = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    canUserAccessIntegrations,
    getCanUserEditTopLevelIntegrationDashboardUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserAccessIntegration,
        canUserEditTopLevelIntegrationDashboardUserPermission,
    ) =>
        !isUserFreemium &&
        (isUserUsingRoleBasedPermissions
            ? canUserEditTopLevelIntegrationDashboardUserPermission
            : canUserAccessIntegration),
);

/**
 * Access to create new top level Integrations
 */
export const getCanUserCreateTopLevelIntegrations = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    canUserAccessIntegrations,
    getCanUserCreateTopLevelIntegrationDashboardUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserAccessIntegration,
        canUserCreateTopLevelIntegrationDashboardUserPermission,
    ) =>
        !isUserFreemium &&
        (isUserUsingRoleBasedPermissions
            ? canUserCreateTopLevelIntegrationDashboardUserPermission
            : canUserAccessIntegration),
);

/**
 * Access to Delete top level integrations
 */
export const getCanUserDeleteTopLevelIntegrations = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    canUserAccessIntegrations,
    getCanUserDeleteTopLevelIntegrationDashboardUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserAccessIntegration,
        canUserDeleteTopLevelIntegrationDashboardUserPermission,
    ) =>
        !isUserFreemium &&
        (isUserUsingRoleBasedPermissions
            ? canUserDeleteTopLevelIntegrationDashboardUserPermission
            : canUserAccessIntegration),
);

/**
 * Access to create event level integrations
 */
export const getCanUserCreateEventLevelIntegrations = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    canUserCreateEventLevelIntegration,
    getCanUserCreateEventLevelIntegrationDashboardUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        getCanUserCreateEventLevelIntegration,
        canUserCreateEventLevelIntegrationDashboardUserPermission,
    ) =>
        !isUserFreemium &&
        (isUserUsingRoleBasedPermissions
            ? canUserCreateEventLevelIntegrationDashboardUserPermission
            : getCanUserCreateEventLevelIntegration),
);

/**
 * Access to View Event Level integrations
 */
export const getCanUserViewEventLevelIntegrations = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserViewEventLevelIntegrationDashboardUserPermission,
    (isUserFreemium, isUserUsingRoleBasedPermissions, canUserViewEventLevelIntegrationDashboardUserPermission) =>
        !isUserFreemium &&
        (isUserUsingRoleBasedPermissions ? canUserViewEventLevelIntegrationDashboardUserPermission : true),
);

/**
 * Access to Edit Event Level Integrations
 */
export const getCanUserEditEventLevelIntegrations = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    canUserEditEventLevelIntegrations,
    getCanUserEditEventLevelIntegrationDashboardUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserEditEventLevelIntegration,
        canUserEditEventLevelIntegrationDashboardUserPermission,
    ) =>
        !isUserFreemium &&
        (isUserUsingRoleBasedPermissions
            ? canUserEditEventLevelIntegrationDashboardUserPermission
            : canUserEditEventLevelIntegration),
);

/**
 * Access to Edit Event Level Zoom Data
 */
export const getCanUserManageZoom = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    canUserEditEventLevelIntegrations,
    getCanUserManageZoomPermission,
    (isUserFreemium, isUserUsingRoleBasedPermissions, canUserEditEventLevelIntegration, canUserManageZoomPermission) =>
        !isUserFreemium &&
        (isUserUsingRoleBasedPermissions ? canUserManageZoomPermission : canUserEditEventLevelIntegration),
);

/**
 * Access to Delete Event Level Integrations
 */
export const getCanUserDeleteEventLevelIntegrations = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    canUserEditEventLevelIntegrations,
    getCanUserDeleteEventLevelIntegrationDashboardUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserEditEventLevelIntegration,
        canUserDeleteEventLevelIntegrationDashboardUserPermission,
    ) =>
        !isUserFreemium &&
        (isUserUsingRoleBasedPermissions
            ? canUserDeleteEventLevelIntegrationDashboardUserPermission
            : canUserEditEventLevelIntegration),
);

//= ===================
//   EVENT TOUCHPOINTS
//= ==================

/*
 * Access to edit event touchpoint
 */
export const getCanUserEditEventPageTouchpoint = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserEditEventPage,
    getCanUserEditEventPageTouchpointUserPermission,
    (isUserFreemium, isUserUsingRoleBasedPermissions, canUserEditEventPage, canUserEditEventPageUserPermission) =>
        isUserFreemium || (isUserUsingRoleBasedPermissions ? canUserEditEventPageUserPermission : canUserEditEventPage),
);

export const getCanUserViewGuestList = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    canUserAccessGuestList,
    getCanUserViewGuestListUserPermission,
    (isUserFreemium, isUserUsingRoleBasedPermissions, canUserAccessGuests, canUserViewGuestListUserPermission) =>
        isUserFreemium || (isUserUsingRoleBasedPermissions ? canUserViewGuestListUserPermission : canUserAccessGuests),
);

export const getCanUserEditGuestList = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    canUserAccessGuestList,
    getCanUserEditGuestListUserPermission,
    (isUserFreemium, isUserUsingRoleBasedPermissions, canUserAccessGuests, canUserEditGuestListUserPermission) =>
        isUserFreemium || (isUserUsingRoleBasedPermissions ? canUserEditGuestListUserPermission : canUserAccessGuests),
);

export const getCanUserCreateGuests = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    canUserAccessGuestList,
    getCanUserCreateGuestUserPermission,
    (isUserFreemium, isUserUsingRoleBasedPermissions, canUserAccessGuests, canUserCreateGuestListUserPermission) =>
        isUserFreemium ||
        (isUserUsingRoleBasedPermissions ? canUserCreateGuestListUserPermission : canUserAccessGuests),
);

export const getCanUserDeleteGuests = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    canUserAccessGuestList,
    getCanUserDeleteGuestsUserPermission,
    (isUserFreemium, isUserUsingRoleBasedPermissions, canUserAccessGuests, canUserDeleteGuestsUserPermission) =>
        isUserFreemium || (isUserUsingRoleBasedPermissions ? canUserDeleteGuestsUserPermission : canUserAccessGuests),
);

export const getCanUserExportGuests = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    canUserAccessGuestList,
    getCanUserExportGuestUserPermission,
    (isUserFreemium, isUserUsingRoleBasedPermissions, canUserAccessGuests, canUserExportGuestListUserPermission) =>
        isUserFreemium ||
        (isUserUsingRoleBasedPermissions ? canUserExportGuestListUserPermission : canUserAccessGuests),
);

export const getCanUserImportGuestsViaCsv = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    canUserAccessGuestList,
    getCanUserImportGuestsViaCsvPermission,
    (isUserFreemium, isUserUsingRoleBasedPermissions, canUserAccessGuests, canUserImportViaCsv) =>
        isUserFreemium || (isUserUsingRoleBasedPermissions ? canUserImportViaCsv : canUserAccessGuests),
);

export const getCanUserViewContactsDashboard = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    canUserAccessContacts,
    getCanUserViewContactsDashboardUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserAccessContact,
        canUserViewContactsDashboardUserPermission,
    ) =>
        isUserFreemium ||
        (isUserUsingRoleBasedPermissions ? canUserViewContactsDashboardUserPermission : canUserAccessContact),
);

export const getCanUserShareGuestList = createSelector(
    getIsUserUsingRoleBasedPermissions,
    canUserAccessGuestList,
    getCanUserShareGuestListUserPermission,
    (isUserUsingRoleBasedPermissions, canUserAccessGuests, canUserShareGuestListUserPermission) =>
        isUserUsingRoleBasedPermissions ? canUserShareGuestListUserPermission : canUserAccessGuests,
);

// ================================= CONFIRMATION TOUCHPOINT ============================== //

/**
 * View the touchpoint for confirmations
 */
export const getCanUserViewConfirmationsTouchpoint = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    canUserAccessConfirmations,
    getCanUserViewConfirmationUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserAccessConfirmationsUserProperties,
        canUserViewConfirmationsUserPermission,
    ) =>
        isUserFreemium ||
        (isUserUsingRoleBasedPermissions
            ? canUserViewConfirmationsUserPermission
            : canUserAccessConfirmationsUserProperties),
);

/**
 * Edit Confirmation Content Page
 */
export const getCanUserEditConfirmationsTouchpoint = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    canUserAccessConfirmations,
    getCanUserEditConfirmationUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserAccessConfirmationsUserProperties,
        canUserEditConfirmationsUserPermission,
    ) =>
        isUserFreemium ||
        (isUserUsingRoleBasedPermissions
            ? canUserEditConfirmationsUserPermission
            : canUserAccessConfirmationsUserProperties),
);

/**
 * Access to view event settings
 * We want to show a disabled tab if using RBAC
 * We want to show a enabled tab if user is freemium
 * We want to hide a tab if user is not using RBAC and !canUserViewEventSettings
 */
export const getCanUserAccessEventSettingsTab = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    canUserAccessEventSettings,
    (isUserFreemium, isUserUsingRoleBasedPermissions, canUserViewEventSettings) =>
        isUserFreemium || (isUserUsingRoleBasedPermissions ? true : canUserViewEventSettings),
);

/**
 * Access to view event settings
 */
export const getCanUserViewEventSettings = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    canUserAccessEventSettings,
    getCanUserViewEventSettingsUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserViewEventSettings,
        canUserViewEventSettingsUserPermission,
    ) =>
        isUserFreemium ||
        (isUserUsingRoleBasedPermissions ? canUserViewEventSettingsUserPermission : canUserViewEventSettings),
);

/*
 * Access to edit event settings
 */
export const getCanUserEditEventSettings = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserEditEventSettingsUserPermission,
    (isUserFreemium, isUserUsingRoleBasedPermissions, canUserEditEventSettingsUserPermission) =>
        isUserFreemium || (isUserUsingRoleBasedPermissions ? canUserEditEventSettingsUserPermission : true),
);

/**
 * Access to edit theme in Theme Dashboard
 */
export const getCanUserEditThemeTab = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserEditThemeUserPermission,
    getCanUserAccessThemesDashboard,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserEditThemeTabUserPermission,
        canUserAccessThemesDashboard,
    ) =>
        isUserFreemium ||
        (isUserUsingRoleBasedPermissions ? canUserEditThemeTabUserPermission : canUserAccessThemesDashboard),
);

/**
 * Access to create theme in Theme Dashboard
 */
export const getCanUserCreateThemes = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserCreateThemesUserPermission,
    getCanUserCreateTheme,
    (isUserFreemium, isUserUsingRoleBasedPermissions, canUserCreateThemesUserPermission, canUserCreateTheme) =>
        isUserFreemium || (isUserUsingRoleBasedPermissions ? canUserCreateThemesUserPermission : canUserCreateTheme),
);

/**
 * Access to delete theme in Theme Dashboard
 */
export const getCanUserDeleteThemes = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserDeleteThemesUserPermission,
    getCanUserAccessThemesDashboard,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserDeleteThemesUserPermission,
        canUserAccessThemesDashboard,
    ) =>
        isUserFreemium ||
        (isUserUsingRoleBasedPermissions ? canUserDeleteThemesUserPermission : canUserAccessThemesDashboard),
);

/**
 * Access to view registration form
 */
export const getCanUserViewRegistrationForm = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessFormsAndTickets,
    getCanUserViewRegistrationFormUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserAccessFormsAndTickets,
        canUserViewRegistrationFormTouchpointUserPermission,
    ) =>
        isUserFreemium ||
        (isUserUsingRoleBasedPermissions
            ? canUserViewRegistrationFormTouchpointUserPermission
            : canUserAccessFormsAndTickets),
);

/**
 * Access to edit registration form
 */
export const getCanUserEditRegistrationForm = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessFormsAndTickets,
    getCanUserEditRegistrationFormUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserAccessFormsAndTickets,
        canUserEditRegistrationFormTouchpointUserPermission,
    ) =>
        isUserFreemium ||
        (isUserUsingRoleBasedPermissions
            ? canUserEditRegistrationFormTouchpointUserPermission
            : canUserAccessFormsAndTickets),
);

/**
 * Access to create registration form
 */
export const getCanUserCreateRegistrationForm = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessFormsAndTickets,
    getCanUserCreateRegistrationFormUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserAccessFormsAndTickets,
        canUserCreateRegistrationFormTouchpointUserPermission,
    ) =>
        isUserFreemium ||
        (isUserUsingRoleBasedPermissions
            ? canUserCreateRegistrationFormTouchpointUserPermission
            : canUserAccessFormsAndTickets),
);

/**
 * Access to delete registration form
 */
export const getCanUserDeleteRegistrationForm = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessFormsAndTickets,
    getCanUserDeleteRegistrationFormUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserAccessFormsAndTickets,
        canUserDeleteRegistrationFormTouchpointUserPermission,
    ) =>
        isUserFreemium ||
        (isUserUsingRoleBasedPermissions
            ? canUserDeleteRegistrationFormTouchpointUserPermission
            : canUserAccessFormsAndTickets),
);

/**
 * Access to view multiple registration forms
 */
export const getCanUserViewMultipleRegistrationForms = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessFormsAndTickets,
    getCanUserViewMultipleRegistrationFormsUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserAccessFormsAndTickets,
        canUserViewMultipleRegistrationFormsTouchpointUserPermission,
    ) =>
        isUserFreemium ||
        (isUserUsingRoleBasedPermissions
            ? canUserViewMultipleRegistrationFormsTouchpointUserPermission
            : canUserAccessFormsAndTickets),
);

/**
 * Access to edit multiple registration forms
 */
export const getCanUserEditMultipleRegistrationForms = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessFormsAndTickets,
    getCanUserEditMultipleRegistrationFormsUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserAccessFormsAndTickets,
        canUserEditMultipleRegistrationFormsTouchpointUserPermission,
    ) =>
        isUserFreemium ||
        (isUserUsingRoleBasedPermissions
            ? canUserEditMultipleRegistrationFormsTouchpointUserPermission
            : canUserAccessFormsAndTickets),
);

/**
 * Access to create multiple registration forms
 */
export const getCanUserCreateMultipleRegistrationForms = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessFormsAndTickets,
    getCanUserCreateMultipleRegistrationFormsUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserAccessFormsAndTickets,
        canUserCreateMultipleRegistrationFormsTouchpointUserPermission,
    ) =>
        isUserFreemium ||
        (isUserUsingRoleBasedPermissions
            ? canUserCreateMultipleRegistrationFormsTouchpointUserPermission
            : canUserAccessFormsAndTickets),
);

/**
 * Access to delete multiple registration forms
 */
export const getCanUserDeleteMultipleRegistrationForms = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessFormsAndTickets,
    getCanUserDeleteMultipleRegistrationFormsUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserAccessFormsAndTickets,
        canUserDeleteMultipleRegistrationFormsTouchpointUserPermission,
    ) =>
        isUserFreemium ||
        (isUserUsingRoleBasedPermissions
            ? canUserDeleteMultipleRegistrationFormsTouchpointUserPermission
            : canUserAccessFormsAndTickets),
);

/**
 * Access to Forms Manager
 */
export const getCanUserAccessFormsManager = createSelector(
    getIsEventMultipleRegistrationFormsEnabled,
    getCanUserViewRegistrationForm,
    getCanUserEditRegistrationForm,
    getCanUserViewMultipleRegistrationForms,
    getCanUserEditMultipleRegistrationForms,
    (
        isEventMultipleRegistrationFormsEnabled,
        canUserViewRegistrationForm,
        canUserEditRegistrationForm,
        canUserViewMultipleRegistrationForms,
        canUserEditMultipleRegistrationForms,
    ) => {
        const canUserAccessRegistrationForm = canUserViewRegistrationForm || canUserEditRegistrationForm;
        const canUserAccessMultipleRegistrationForms =
            canUserViewMultipleRegistrationForms || canUserEditMultipleRegistrationForms;

        return isEventMultipleRegistrationFormsEnabled
            ? canUserAccessRegistrationForm || canUserAccessMultipleRegistrationForms
            : canUserAccessRegistrationForm;
    },
);

/**
 * Access to view tickets tab
 */
export const getCanUserViewTicketsTab = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessFormsAndTickets,
    getCanUserViewTicketsTabUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserAccessFormsAndTickets,
        canUserViewTicketsTabUserPermission,
    ) =>
        cond([
            [() => isUserFreemium, () => true],
            [() => isUserUsingRoleBasedPermissions, () => canUserViewTicketsTabUserPermission],
            [() => canUserAccessFormsAndTickets, () => true],
            [() => true, () => false],
        ])(),
);

/**
 * Access to edit tickets tab
 */
export const getCanUserEditTicketsTab = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessFormsAndTickets,
    getCanUserEditTicketsTabUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserAccessFormsAndTickets,
        canUserEditTicketsTabUserPermission,
    ) =>
        cond([
            [() => isUserFreemium, () => true],
            [() => isUserUsingRoleBasedPermissions, () => canUserEditTicketsTabUserPermission],
            [() => canUserAccessFormsAndTickets, () => true],
            [() => true, () => false],
        ])(),
);

/**
 * Check if browser is fetching access to edit tickets tab
 */
export const getIsFetchingCanUserEditTicketsTab = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getIsFetchingCanUserEditTicketsTabUserPermission,
    (isUserFreemium, isUserUsingRoleBasedPermissions, isFetchingCanUserEditTicketsTabUserPermission) =>
        !isUserFreemium && isUserUsingRoleBasedPermissions ? isFetchingCanUserEditTicketsTabUserPermission : false,
);

/**
 * Access to create tickets tab
 */
export const getCanUserCreateTicketsTab = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessFormsAndTickets,
    getCanUserCreateTicketsTabUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserAccessFormsAndTickets,
        canUserCreateTicketsTabUserPermission,
    ) =>
        cond([
            [() => isUserFreemium, () => true],
            [() => isUserUsingRoleBasedPermissions, () => canUserCreateTicketsTabUserPermission],
            [() => canUserAccessFormsAndTickets, () => true],
            [() => true, () => false],
        ])(),
);

/**
 * Access to delete tickets tab
 */
export const getCanUserDeleteTicketsTab = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessFormsAndTickets,
    getCanUserDeleteTicketsTabUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserAccessFormsAndTickets,
        canUserDeleteTicketsTabUserPermission,
    ) =>
        cond([
            [() => isUserFreemium, () => true],
            [() => isUserUsingRoleBasedPermissions, () => canUserDeleteTicketsTabUserPermission],
            [() => canUserAccessFormsAndTickets, () => true],
            [() => true, () => false],
        ])(),
);

/**
 * Access to create events
 */
export const getCanUserCreateEvents = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    canUserAccessEventOverview,
    getCanUserCreateEventsUserPermission,
    (isUserFreemium, isUserUsingRoleBasedPermissions, canAccessEventOverview, canUserCreateEventsUserPermission) =>
        isUserFreemium ||
        (isUserUsingRoleBasedPermissions ? canUserCreateEventsUserPermission : canAccessEventOverview),
);

export const getCanFreemiumUserExportGuests = createSelector(
    getIsFreemiumFeatureRestrictionEnabled,
    getIsUserFreemium,
    (isFreemiumFeatureRestrictionEnabled, isUserFreemium) => isUserFreemium && !isFreemiumFeatureRestrictionEnabled,
);

const getCanFreemiumUserExportEvents = createSelector(
    getIsFreemiumFeatureRestrictionEnabled,
    getIsUserFreemium,
    (isFreemiumFeatureRestrictionEnabled, isUserFreemium) => isUserFreemium && !isFreemiumFeatureRestrictionEnabled,
);

export const getCanUserExportEvents = createSelector(
    getIsUserFreemium,
    getCanFreemiumUserExportEvents,
    getIsUserUsingRoleBasedPermissions,
    canUserAccessEventOverview,
    getCanUserExportEventsUserPermission,
    (
        isUserFreemium,
        canFreemiumUserExportEvents,
        isUserUsingRoleBasedPermissions,
        canAccessEventOverview,
        canUserExportEventsUserPermission,
    ) =>
        isUserFreemium
            ? canFreemiumUserExportEvents
            : isUserUsingRoleBasedPermissions
            ? canUserExportEventsUserPermission
            : canAccessEventOverview,
);

/**
 * Can publish/unpublish events
 */
export const getCanUserPublishEvents = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    canUserModifyPublished,
    getCanUserPublishEventsUserPermission,
    (isUserFreemium, isUserUsingRoleBasedPermissions, canUserPublish, canUserPublishEventsUserPermission) =>
        !isUserFreemium && isUserUsingRoleBasedPermissions ? canUserPublishEventsUserPermission : canUserPublish,
);

export const getCanFreemiumUserDuplicateEvents = createSelector(
    getIsFreemiumFeatureRestrictionEnabled,
    getIsUserFreemium,
    (isFreemiumFeatureRestrictionEnabled, isUserFreemium) => isUserFreemium && !isFreemiumFeatureRestrictionEnabled,
);

export const getShouldUserSeeFreemiumUpsell = createSelector(
    getIsFreemiumFeatureRestrictionEnabled,
    getIsUserFreemium,
    (isFreemiumFeatureRestrictionEnabled, isUserFreemium) => isUserFreemium && isFreemiumFeatureRestrictionEnabled,
);

/**
 * Can actually duplicate events
 */
export const getCanUserDuplicateEvents = createSelector(
    getIsUserFreemium,
    getCanFreemiumUserDuplicateEvents,
    getIsUserUsingRoleBasedPermissions,
    getCanUserDuplicateEventsUserPermission,
    getCanUserDuplicateEventsViaUserProperties,
    (
        isUserFreemium,
        canFreemiumUserDuplicateEvents,
        isUserUsingRoleBasedPermissions,
        canUserDuplicateEventsUserPermission,
        canUserDuplicateEvents,
    ) =>
        isUserFreemium
            ? canFreemiumUserDuplicateEvents
            : isUserUsingRoleBasedPermissions
            ? canUserDuplicateEventsUserPermission
            : canUserDuplicateEvents,
);

/**
 * Can see event duplication functionality
 */
export const getCanUserSeeEventDuplication = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserDuplicateEvents,
    (isUserFreemium, isUserUsingRoleBasedPermissions, canUserDuplicateEvents) =>
        isUserFreemium || isUserUsingRoleBasedPermissions || canUserDuplicateEvents,
);

/**
 * Can view add to hub button
 */
export const getCanUserAddToHub = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAddToHubViaUserProperties,
    getCanUserAddToHubUserPermission,
    (isUserFreemium, isUserUsingRoleBasedPermissions, canUserAddToHub, canUserAddToHubUserPermission) =>
        !isUserFreemium && (isUserUsingRoleBasedPermissions ? canUserAddToHubUserPermission : canUserAddToHub),
);

/**
 * Can delete events
 */
export const getCanUserDeleteEvents = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserDeleteEventsViaUserProperties,
    getCanUserDeleteEventsUserPermission,
    (isUserFreemium, isUserUsingRoleBasedPermissions, canUserDeleteEvents, canUserDeleteEventsUserPermission) =>
        isUserFreemium || (isUserUsingRoleBasedPermissions ? canUserDeleteEventsUserPermission : canUserDeleteEvents),
);

/**
 * Can view the self-serve global brand manager
 */
export const getCanUserViewGlobalBrandManager = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessGlobalBrandManager,
    getCanUserViewGlobalBrandManagerUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserAccessGlobalBrandManager,
        canUserViewGlobalBrandManagerUserPermission,
    ) =>
        !isUserFreemium && isUserUsingRoleBasedPermissions
            ? canUserViewGlobalBrandManagerUserPermission
            : canUserAccessGlobalBrandManager,
);

/**
 * Can edit a brand in the self-serve global brand manager
 */
export const getCanUserEditGlobalBrandManager = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessGlobalBrandManager,
    getCanUserEditGlobalBrandManagerUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserAccessGlobalBrandManager,
        canUserEditGlobalBrandManagerUserPermission,
    ) =>
        !isUserFreemium && isUserUsingRoleBasedPermissions
            ? canUserEditGlobalBrandManagerUserPermission
            : canUserAccessGlobalBrandManager,
);

/**
 * Can create a brand in the self-serve global brand manager
 */
export const getCanUserCreateGlobalBrandManager = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessGlobalBrandManager,
    getCanUserCreateGlobalBrandManagerUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserAccessGlobalBrandManager,
        canUserCreateGlobalBrandManagerUserPermission,
    ) =>
        !isUserFreemium && isUserUsingRoleBasedPermissions
            ? canUserCreateGlobalBrandManagerUserPermission
            : canUserAccessGlobalBrandManager,
);

/**
 * Can publish a brand in the self-serve global brand manager
 */
export const getCanUserPublishGlobalBrandManager = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessGlobalBrandManager,
    getCanUserPublishGlobalBrandManagerUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserAccessGlobalBrandManager,
        canUserPublishGlobalBrandManagerUserPermission,
    ) =>
        !isUserFreemium && isUserUsingRoleBasedPermissions
            ? canUserPublishGlobalBrandManagerUserPermission
            : canUserAccessGlobalBrandManager,
);

/**
 * Permission to show the option to allow users to register
 * for overlapping sessions in event settings
 */
export const getCanUserViewSessionsOverlapSetting = createSelector(
    getIsUserFreemium,
    getIsSessionLegacyEvent,
    (isUserFreemium, isSessionLegacyEvent) =>
        cond([
            [() => isUserFreemium, () => false],
            [() => isSessionLegacyEvent, () => true],
            [() => true, () => false],
        ])(),
);

/**
 * Permission to make sessions tab visible
 */
export const getCanUserViewSessionsTab = createSelector(
    getIsUserFreemium,
    getIsMultisessionEvent,
    (isUserFreemium, isMultisessionEvent) =>
        cond([
            [() => isUserFreemium, () => false],
            [() => isMultisessionEvent, () => true],
            [() => true, () => false],
        ])(),
);

/**
 * Permission to enable sessions tab and sessions manager
 */
export const getCanUserAccessSessionsTab = createSelector(
    getIsUserFreemium,
    getIsMultisessionEvent,
    getIsUserUsingRoleBasedPermissions,
    getCanUserViewSessionsTabUserPermission,
    (isUserFreemium, isMultisessionEvent, isUserUsingRoleBasedPermissions, canUserViewSessionsTabUserPermission) =>
        cond([
            [() => isUserFreemium, () => false],
            [() => isUserUsingRoleBasedPermissions, () => canUserViewSessionsTabUserPermission],
            [() => !isUserUsingRoleBasedPermissions, () => isMultisessionEvent],
            [() => true, () => false],
        ])(),
);

/**
 * Access to edit sessions
 */
export const getCanUserEditSessions = createSelector(
    getIsUserFreemium,
    getIsMultisessionEvent,
    getIsUserUsingRoleBasedPermissions,
    getCanUserEditSessionsTabUserPermission,
    (isUserFreemium, isMultisessionEvent, isUserUsingRoleBasedPermissions, canUserEditSessionsTabUserPermission) =>
        cond([
            [() => isUserFreemium, () => false],
            [() => isUserUsingRoleBasedPermissions, () => canUserEditSessionsTabUserPermission],
            [() => !isUserUsingRoleBasedPermissions, () => isMultisessionEvent],
            [() => true, () => false],
        ])(),
);

/**
 * Access to create sessions
 */
export const getCanUserCreateSessions = createSelector(
    getIsUserFreemium,
    getIsMultisessionEvent,
    getIsUserUsingRoleBasedPermissions,
    getCanUserCreateSessionsTabUserPermission,
    (isUserFreemium, isMultisessionEvent, isUserUsingRoleBasedPermissions, canUserCreateSessionsTabUserPermission) =>
        cond([
            [() => isUserFreemium, () => false],
            [() => isUserUsingRoleBasedPermissions, () => canUserCreateSessionsTabUserPermission],
            [() => !isUserUsingRoleBasedPermissions, () => isMultisessionEvent],
            [() => true, () => false],
        ])(),
);

/**
 * Access to delete sessions
 */
export const getCanUserDeleteSessions = createSelector(
    getIsUserFreemium,
    getIsMultisessionEvent,
    getIsUserUsingRoleBasedPermissions,
    getCanUserDeleteSessionsTabUserPermission,
    (isUserFreemium, isMultisessionEvent, isUserUsingRoleBasedPermissions, canUserDeleteSessionsTabUserPermission) =>
        cond([
            [() => isUserFreemium, () => false],
            [() => isUserUsingRoleBasedPermissions, () => canUserDeleteSessionsTabUserPermission],
            [() => !isUserUsingRoleBasedPermissions, () => isMultisessionEvent],
            [() => true, () => false],
        ])(),
);

/**
 * Access to analytics tab
 */
export const getCanUserAccessAnalytics = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessAnalyticsUserProperties,
    getCanUserAccessAnalyticsUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserAccessAnalyticsUserProperties,
        canUserAccessAnalyticsUserPermission,
    ) =>
        isUserFreemium ||
        (isUserUsingRoleBasedPermissions ? canUserAccessAnalyticsUserPermission : canUserAccessAnalyticsUserProperties),
);

export const getCanUserAccessAttendanceInsights = createSelector(
    getIsUserFreemium,
    getHasOrgDisabledArtificialIntelligenceFeatures,
    getIsCurrentEventTicketed,
    getCanUserAccessAnalytics,
    getIsAttendancePredictorEnabled,
    (
        isUserFreemium,
        hasOrgDisabledArtificialIntelligenceFeatures,
        isTicketedEvent,
        canUserAccessAnalytics,
        isAttendancePredictorEnabled,
    ) =>
        !isUserFreemium &&
        !hasOrgDisabledArtificialIntelligenceFeatures &&
        !isTicketedEvent &&
        canUserAccessAnalytics &&
        isAttendancePredictorEnabled,
);

/**
 * Ability to update workflow status
 */
export const getCanUpdateWorkflowStatus = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUpdateWorkflowStatusUserProperties,
    getCanUpdateWorkflowStatusUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUpdateWorkflowStatusUserProperties,
        canUpdateWorkflowStatusUserPermission,
    ) =>
        !isUserFreemium &&
        (isUserUsingRoleBasedPermissions
            ? canUpdateWorkflowStatusUserPermission
            : canUpdateWorkflowStatusUserProperties),
);

/**
 * Ability to edit vanity url
 */
export const getCanEditVanityUrl = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserSetVanityUrl,
    getCanUserEditVanityUrlUserPermission,
    (isUserFreemium, isUserUsingRoleBasedPermissions, canUserSetVanityUrl, canUserEditVanityUrlUserPermission) =>
        isUserFreemium || (isUserUsingRoleBasedPermissions ? canUserEditVanityUrlUserPermission : canUserSetVanityUrl),
);

/**
 * Ability to edit event url
 */
export const getCanEditEventUrl = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    canUserAccessEventSettings,
    getCanUserEditEventUrlUserPermission,
    (isUserFreemium, isUserUsingRoleBasedPermissions, canUserViewEventSettings, canUserEditEventUrlUserPermission) =>
        isUserFreemium ||
        (isUserUsingRoleBasedPermissions ? canUserEditEventUrlUserPermission : canUserViewEventSettings),
);

/**
 * Access to view virtual event touchpoint
 */
export const getCanUserViewVirtualEventTouchpoint = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessVirtualEventTouchpointUserProperty,
    getCanUserViewVirtualEventTouchpointUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserAccessVirtualEventTouchpointUserProperty,
        canUserViewVirtualEventTouchpointUserPermission,
    ) =>
        isUserFreemium ||
        (isUserUsingRoleBasedPermissions
            ? canUserViewVirtualEventTouchpointUserPermission
            : canUserAccessVirtualEventTouchpointUserProperty),
);

/**
 * Access to create virtual event touchpoint
 */
export const getCanUserCreateVirtualEventTouchpoint = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessVirtualEventTouchpointUserProperty,
    getCanUserCreateVirtualEventTouchpointUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserAccessVirtualEventTouchpointUserProperty,
        canUserCreateVirtualEventTouchpointUserPermission,
    ) =>
        !isUserFreemium &&
        (isUserUsingRoleBasedPermissions
            ? canUserCreateVirtualEventTouchpointUserPermission
            : canUserAccessVirtualEventTouchpointUserProperty),
);

/**
 * Access to edit virtual event touchpoint
 */
export const getCanUserEditVirtualEventTouchpoint = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getIsUserVirtualViewOnly,
    getCanUserEditVirtualEventTouchpointUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        isUserVirtualViewOnly,
        canUserEditVirtualEventTouchpointUserPermission,
    ) =>
        !isUserFreemium &&
        (isUserUsingRoleBasedPermissions ? canUserEditVirtualEventTouchpointUserPermission : !isUserVirtualViewOnly),
);

/**
 * Access to delete virtual event touchpoint
 */
export const getCanUserDeleteVirtualEventTouchpoint = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessVirtualEventTouchpointUserProperty,
    getCanUserDeleteVirtualEventTouchpointUserPermission,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserAccessVirtualEventTouchpointUserProperty,
        canUserDeleteVirtualEventTouchpointUserPermission,
    ) =>
        !isUserFreemium &&
        (isUserUsingRoleBasedPermissions
            ? canUserDeleteVirtualEventTouchpointUserPermission
            : canUserAccessVirtualEventTouchpointUserProperty),
);

/**
 * Controls if user can see the product roadmap embedded view on app level navigation
 * Enterprise --> can view product roadmap
 * Freemium --> cannot view product roadmap
 */
export const getCanUserViewProductRoadmap = createSelector(getIsUserFreemium, (isUserFreemium) => !isUserFreemium);

/**
 * Access to org settings
 */

export const getCanUserAccessDataAnonymizationSettings = createSelector(
    getIsDataAnonymizationEnabled,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessDataAnonymizationUserPermission,
    (isDataAnonymizationEnabled, isUserUsingRoleBasedPermissions, canUserAccessDataAnonymizationUserPermission) =>
        cond([
            [() => !isDataAnonymizationEnabled, () => false],
            [() => !isUserUsingRoleBasedPermissions, () => false],
            [() => isUserUsingRoleBasedPermissions, () => canUserAccessDataAnonymizationUserPermission],
        ])(),
);

export const getCanUserAccessPrivacyManagementEventSettings = createSelector(
    getIsPrivacyManagementEnabled,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessPrivacyManagementEventSettingsPermission,
    (
        isPrivacyManagementEnabled,
        isUserUsingRoleBasedPermissions,
        canUserAccessPrivacyManagementEventSettingsPermission,
    ) =>
        cond([
            [() => !isPrivacyManagementEnabled, () => false],
            [() => !isUserUsingRoleBasedPermissions, () => false],
            [() => isUserUsingRoleBasedPermissions, () => canUserAccessPrivacyManagementEventSettingsPermission],
        ])(),
);

export const getCanUserAccessPrivacyManagementOrgSettings = createSelector(
    getIsPrivacyManagementEnabled,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessPrivacyManagementOrgSettingsPermission,
    (
        isPrivacyManagementEnabled,
        isUserUsingRoleBasedPermissions,
        canUserAccessPrivacyManagementOrgSettingsPermission,
    ) =>
        cond([
            [() => !isPrivacyManagementEnabled, () => false],
            [() => !isUserUsingRoleBasedPermissions, () => false],
            [() => isUserUsingRoleBasedPermissions, () => canUserAccessPrivacyManagementOrgSettingsPermission],
        ])(),
);

export const getCanUserAccessDoubleOptInOrgSettings = createSelector(
    getIsDoubleOptInEnabled,
    getIsUserAdmin,
    getCanUserAccessTeam,
    (isDoubleOptInEnabled, isUserAdmin, canUserAccessTeam) => isDoubleOptInEnabled && isUserAdmin && canUserAccessTeam,
);

export const getCanUserAccessOrgSettings = createSelector(
    getCanUserAccessDataAnonymizationSettings,
    getCanUserAccessPrivacyManagementOrgSettings,
    getCanUserAccessDoubleOptInOrgSettings,
    (canAccessDataAnonymization, canAccessPrivacyManagement, canUserAccessDoubleOptIn) =>
        canAccessDataAnonymization || canAccessPrivacyManagement || canUserAccessDoubleOptIn,
);

/*
 * Access to create event via new frontend flow
 * /events/create/frontend
 */
export const getCanUserAccessEventCreateFrontend = createSelector(getIsUserAdmin, (isUserAdmin) => isUserAdmin);

/*
 * Access to edit CMS pages using code Editor
 */
export const getCanUserAccessCMSCodeEditor = createSelector(
    getIsUserFreemium,
    getIsUserUsingRoleBasedPermissions,
    getCanUserAccessCMSCodeEditorUserPermission,
    getIsCMSCodeEditorEnabled,
    getIsUserInDesignerPro,
    (
        isUserFreemium,
        isUserUsingRoleBasedPermissions,
        canUserAccessCMSCodeEditorPermission,
        isCMSCodeEditorEnabled,
        isUserInDesignerPro,
    ) =>
        !isUserFreemium &&
        isCMSCodeEditorEnabled &&
        (isUserUsingRoleBasedPermissions ? canUserAccessCMSCodeEditorPermission : isUserInDesignerPro),
);
